/* ──────────────────────────────────────────────────────────────────────
   /free-guide  +  /magazine — Faitharama Quarterly Magazine subscription
   ──────────────────────────────────────────────────────────────────────
   The lead magnet is the FREE quarterly magazine. Visitors give name+email,
   we add them to the magazine list. Backend uses the existing
   /api/leads/free-guide endpoint with source="magazine-quarterly".

   Cover: styled magazine cover mock-up. Mike will upload a real cover
   image when issue 1 is finalized — swap COVER below to the artifact URL.
   ────────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { Mail, Lock, CheckCircle2, Loader2, BookOpen } from "lucide-react";

const G    = "#2E9F3A";
const O    = "#F39A1F";
const RED  = "#C9202E";
const SKY  = "#E0F2FE";
const INK  = "#0F172A";

const API  = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Real magazine cover uploaded by Mike — Issue 1 (Spring 2026). */
const COVER_IMAGE = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/2x3kaylx_FAITHARAMA%20FAITH%20DRIVEN%20QUARTERLY%20MAGAZINE.png";

const TEASERS = [
  "Important articles for Christian entrepreneurs",
  "New products and partner spotlights — vetted by Mike personally",
  "How to build a successful Christian business — step by step",
  "Real stories from members earning their first dollars online",
  "Wellness, faith, and family — all in one issue",
];

export default function FreeGuidePage() {
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    document.title = "Free Faitharama Quarterly Magazine — Subscribe";
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads/free-guide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          source: "magazine-quarterly",
        }),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-white" data-testid="magazine-subscribe-page">
      <SiteNav />

      <section style={{ background: SKY }} className="py-10 md:py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-8">

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">

            {/* LEFT — Magazine cover (real image if uploaded, otherwise styled mock) */}
            <div className="flex justify-center md:justify-end order-2 md:order-1">
              {COVER_IMAGE ? (
                <img
                  src={COVER_IMAGE}
                  alt="Faitharama Faith-Driven Quarterly — Issue 1"
                  data-testid="magazine-cover-image"
                  className="block h-auto rounded-lg shadow-2xl"
                  style={{ width: "min(400px, 85vw)" }}
                />
              ) : (
                <MagazineCoverMock />
              )}
            </div>

            {/* RIGHT — Headline + form */}
            <div className="order-1 md:order-2">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-3" style={{ color: RED }}>
                ★ FREE QUARTERLY MAGAZINE · ISSUE #1 COMING SOON
              </div>

              <h1 className="font-serif-display uppercase leading-[0.95] mb-5"
                  style={{ color: INK, fontSize: "clamp(2.2rem, 4.8vw, 3.6rem)" }}>
                Get Our New<br/>
                <span style={{ color: G }}>Faitharama</span><br/>
                <span style={{ color: O }}>Faith-Driven</span><br/>
                Quarterly Magazine.
              </h1>

              <p className="text-lg md:text-2xl mb-6 leading-relaxed" style={{ color: INK }}>
                See what we&apos;re about. Important articles. New products. How to build a successful Christian business — delivered to your inbox four times a year. Free, forever.
              </p>

              {/* Teasers */}
              <ul className="space-y-3 mb-7" data-testid="magazine-teasers">
                {TEASERS.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-base md:text-lg leading-relaxed" style={{ color: INK }}>
                    <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: G }} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              {/* ───── FORM ───── */}
              {!success ? (
                <form
                  onSubmit={handleSubmit}
                  data-testid="magazine-subscribe-form"
                  className="bg-white rounded-2xl shadow-xl p-5 md:p-6 border-4"
                  style={{ borderColor: G }}
                >
                  <div className="text-center font-bold uppercase text-base md:text-lg tracking-[0.16em] mb-4" style={{ color: INK }}>
                    Subscribe Free — Send Me The Magazine
                  </div>

                  <input
                    type="text" required value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your First Name"
                    data-testid="magazine-name-input"
                    className="w-full px-4 py-3.5 mb-3 rounded-md text-lg border-2 focus:outline-none focus:ring-2"
                    style={{ borderColor: "#cbd5e1", color: INK }}
                  />
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your Best Email Address"
                    data-testid="magazine-email-input"
                    className="w-full px-4 py-3.5 mb-4 rounded-md text-lg border-2 focus:outline-none focus:ring-2"
                    style={{ borderColor: "#cbd5e1", color: INK }}
                  />

                  <button
                    type="submit" disabled={loading}
                    data-testid="magazine-submit-btn"
                    className="w-full px-6 py-4 rounded-md text-lg md:text-xl font-bold uppercase tracking-[0.16em] text-white shadow-lg hover:scale-[1.02] transition flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: O }}
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Subscribing…</>
                    ) : (
                      <><Mail className="w-5 h-5" /> Subscribe Me Free</>
                    )}
                  </button>

                  {error && (
                    <div className="mt-3 text-base font-bold text-center" style={{ color: RED }}>
                      {error}
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-center gap-2 text-sm" style={{ color: INK, opacity: 0.75 }}>
                    <Lock className="w-4 h-4" />
                    100% Private. We never share or sell your email.
                  </div>
                </form>
              ) : (
                <div
                  className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border-4 text-center"
                  style={{ borderColor: G }}
                  data-testid="magazine-subscribe-success"
                >
                  <CheckCircle2 className="w-14 h-14 mx-auto mb-3" style={{ color: G }} />
                  <h2 className="font-serif-display text-2xl md:text-3xl uppercase mb-3" style={{ color: INK }}>
                    You&apos;re Subscribed, {name.split(' ')[0]}!
                  </h2>
                  <p className="text-base md:text-lg" style={{ color: INK }}>
                    Issue #1 of <strong>Faitharama Faith-Driven Quarterly</strong> is on its way. We&apos;ll send it to <strong>{email}</strong> the moment it&apos;s ready.
                  </p>
                  <p className="text-sm italic mt-4" style={{ color: INK, opacity: 0.7 }}>
                    Add hello@faitharama.com to your contacts so we don&apos;t land in spam.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   MAGAZINE COVER MOCK
   Styled placeholder until Mike uploads the real Issue #1 cover.
   Designed to look like a real Christian magazine masthead.
   ──────────────────────────────────────────────────────────────────── */
function MagazineCoverMock() {
  return (
    <div
      data-testid="magazine-cover-mock"
      className="relative rounded-lg shadow-2xl overflow-hidden"
      style={{
        width: "min(380px, 85vw)",
        aspectRatio: "1 / 1.36",
        background: "linear-gradient(180deg,#0F172A 0%,#0F172A 38%,#E0F2FE 38%,#FFFFFF 100%)",
      }}
    >
      {/* Top issue strip */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-2.5 text-white text-[10px] tracking-[0.22em] uppercase font-bold"
           style={{ background: "#C9202E" }}>
        <span>Issue 01 · Q1 2026</span>
        <span>Free</span>
      </div>

      {/* Masthead */}
      <div className="absolute top-7 left-0 right-0 px-5 text-center text-white">
        <div className="font-serif-display uppercase leading-[0.95]"
             style={{ fontSize: "clamp(1.6rem, 4.2vw, 2.4rem)" }}>
          <span style={{ color: G }}>FAITH</span>
          <span style={{ color: O }}>ARAMA</span>
        </div>
        <div className="text-[9px] tracking-[0.32em] uppercase font-bold mt-1 opacity-90">
          The Faith-Driven Quarterly
        </div>
      </div>

      {/* Hero photo zone (gold cross + radial) */}
      <div className="absolute"
           style={{ top: "30%", left: "8%", right: "8%", bottom: "32%" }}>
        <div className="w-full h-full rounded-md flex items-center justify-center"
             style={{
               background:
                 "radial-gradient(closest-side, rgba(243,154,31,0.85) 0%, rgba(243,154,31,0) 70%), #E0F2FE",
             }}>
          <BookOpen className="w-20 h-20" style={{ color: "#fff" }} strokeWidth={1.3} />
        </div>
      </div>

      {/* Headline strap */}
      <div className="absolute"
           style={{ bottom: "8%", left: "8%", right: "8%", color: INK }}>
        <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: RED }}>
          ★ Inside This Issue
        </div>
        <div className="font-serif-display uppercase leading-[1.0] mt-1"
             style={{ fontSize: "clamp(0.95rem, 2.2vw, 1.25rem)" }}>
          Building A Successful<br />
          <span style={{ color: G }}>Christian Business</span><br />
          From Your Kitchen Table
        </div>
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold mt-2 opacity-75">
          + Vetted Products · Faith · Wellness · Income
        </div>
      </div>
    </div>
  );
}
